/**
 * Sand Heightmap Generation — rolling noise hills enveloping wireframe skeleton.
 *
 * Algorithm:
 * 1. Build wireframe profile (topmost + bottommost wireframe cell per column)
 * 2. Multi-octave sine noise heightmap for sand surface (seeded from terrain RNG)
 * 3. Compute sand bottom per column (below wireframe bottom + depth)
 * 4. Fill ALL empty cells between sand top and sand bottom as sand
 *    (wireframe cells stay — they get fully buried inside sand)
 * 5. Gap bridging: interpolate across wireframe gaps
 * 6. Edge taper at stage boundaries
 * 7. CA cleanup (remove isolated, fill gaps)
 *
 * Result: wireframe is completely hidden inside rolling sand terrain at game start.
 * Only deep craters expose the wireframe skeleton underneath.
 */

import { TERRAIN, CELL, SAND_GEN } from '../constants';
import { getGrid, removeThinSand } from './grid';
import { random } from './rng';
import type { WireframeLayout } from '../types';

const { CELL_SIZE } = TERRAIN;

/**
 * Generate sand enveloping all wireframe cells in the grid.
 * Call after rasterizeWireframe() has written CELL.WIREFRAME cells.
 *
 * If layout is provided, floating platforms are excluded from sand generation
 * so they remain visible as distinct structures above the ground.
 */
export function generateSandHeightmap(layout?: WireframeLayout): void {
  const grid = getGrid();
  if (!grid) return;

  const gridHeight = TERRAIN.GRID_HEIGHT;

  // Build set of grid columns belonging to floating platforms (skip sand for these)
  const platformColumns = new Set<number>();
  if (layout) {
    for (const plat of layout.platforms) {
      const startGx = Math.floor(plat.x / CELL_SIZE);
      const endGx = Math.ceil((plat.x + plat.width) / CELL_SIZE);
      for (let gx = startGx; gx <= endGx; gx++) {
        if (gx >= 0 && gx < TERRAIN.GRID_WIDTH) platformColumns.add(gx);
      }
    }
  }

  // --- 1. Build wireframe profile ---
  // For each column, find topmost and bottommost wireframe cells
  const wireTop = new Int16Array(TERRAIN.GRID_WIDTH);
  const wireBottom = new Int16Array(TERRAIN.GRID_WIDTH);
  wireTop.fill(-1);
  wireBottom.fill(-1);

  for (let gx = 0; gx < TERRAIN.GRID_WIDTH; gx++) {
    for (let gy = 0; gy < gridHeight; gy++) {
      if (grid[gy * TERRAIN.GRID_WIDTH + gx] === CELL.WIREFRAME) {
        if (wireTop[gx] < 0) wireTop[gx] = gy;
        wireBottom[gx] = gy;
      }
    }
  }

  // --- 2. Generate noise heightmap ---
  const phase1 = random() * Math.PI * 20;
  const phase2 = random() * Math.PI * 20;
  const phase3 = random() * Math.PI * 20;

  // Find stage boundaries (leftmost/rightmost wireframe columns)
  let stageLeft: number = TERRAIN.GRID_WIDTH;
  let stageRight = 0;
  for (let gx = 0; gx < TERRAIN.GRID_WIDTH; gx++) {
    if (wireTop[gx] >= 0) {
      if (gx < stageLeft) stageLeft = gx;
      if (gx > stageRight) stageRight = gx;
    }
  }
  if (stageLeft >= stageRight) return;

  // --- 3. Compute sand surface (top) and sand bottom per column ---
  const sandTop = new Int16Array(TERRAIN.GRID_WIDTH);
  const sandBottom = new Int16Array(TERRAIN.GRID_WIDTH);
  sandTop.fill(-1);
  sandBottom.fill(-1);

  for (let gx = stageLeft; gx <= stageRight; gx++) {
    if (wireTop[gx] < 0) continue; // gap column — handled in bridging pass
    if (platformColumns.has(gx)) continue; // platform column — keep clean

    // Multi-octave sine noise for top surface
    const noise =
      Math.sin(gx * SAND_GEN.OCTAVE1_FREQ + phase1) * SAND_GEN.OCTAVE1_AMP +
      Math.sin(gx * SAND_GEN.OCTAVE2_FREQ + phase2) * SAND_GEN.OCTAVE2_AMP +
      Math.sin(gx * SAND_GEN.OCTAVE3_FREQ + phase3) * SAND_GEN.OCTAVE3_AMP;

    // Sand top = wireframe top - thickness - noise (lower gy = higher terrain)
    const top = Math.round(wireTop[gx] - SAND_GEN.TOP_THICKNESS - noise);
    sandTop[gx] = Math.max(0, top);

    // Sand bottom = wireframe bottom + depth (extend below wireframe)
    const bottom = Math.round(wireBottom[gx] + SAND_GEN.BOTTOM_THICKNESS);
    sandBottom[gx] = Math.min(gridHeight - 1, bottom);
  }

  // --- 4. Gap bridging ---
  // Columns without wireframe interpolate sand heights from nearest wireframe columns.
  for (let gx = stageLeft; gx <= stageRight; gx++) {
    if (sandTop[gx] >= 0) continue;

    // Find nearest left and right columns with sand profile
    let leftGx = -1;
    let rightGx = -1;
    for (let scan = gx - 1; scan >= stageLeft; scan--) {
      if (sandTop[scan] >= 0) { leftGx = scan; break; }
    }
    for (let scan = gx + 1; scan <= stageRight; scan++) {
      if (sandTop[scan] >= 0) { rightGx = scan; break; }
    }

    // Only bridge reasonably small gaps (< 40 columns = ~80px)
    const maxGap = 40;
    if (leftGx >= 0 && rightGx >= 0 && (rightGx - leftGx) < maxGap) {
      const t = (gx - leftGx) / (rightGx - leftGx);
      sandTop[gx] = Math.round(
        sandTop[leftGx] + (sandTop[rightGx] - sandTop[leftGx]) * t,
      );
      sandBottom[gx] = Math.round(
        sandBottom[leftGx] + (sandBottom[rightGx] - sandBottom[leftGx]) * t,
      );
    }
  }

  // --- 5. Edge taper ---
  const taperCells = SAND_GEN.EDGE_TAPER_CELLS;
  for (let gx = stageLeft; gx <= stageRight; gx++) {
    if (sandTop[gx] < 0) continue;

    const distFromLeft = gx - stageLeft;
    const distFromRight = stageRight - gx;
    const edgeDist = Math.min(distFromLeft, distFromRight);

    if (edgeDist < taperCells) {
      const taper = edgeDist / taperCells;
      if (wireTop[gx] >= 0) {
        // Blend top toward wireframe top (thin at edges)
        sandTop[gx] = Math.round(
          wireTop[gx] + (sandTop[gx] - wireTop[gx]) * taper,
        );
        // Blend bottom toward wireframe bottom (thin at edges)
        sandBottom[gx] = Math.round(
          wireBottom[gx] + (sandBottom[gx] - wireBottom[gx]) * taper,
        );
      }
    }
  }

  // --- 5.5. Surface smoothing ---
  // Neighbor-average pass to reduce staircase artifacts from Math.round()
  for (let iter = 0; iter < SAND_GEN.SURFACE_SMOOTH_PASSES; iter++) {
    for (let gx = stageLeft + 1; gx < stageRight; gx++) {
      if (sandTop[gx] < 0) continue;
      const left = sandTop[gx - 1];
      const right = sandTop[gx + 1];
      if (left < 0 || right < 0) continue;
      const avg = (left + sandTop[gx] + right) / 3;
      sandTop[gx] = Math.round(avg);
    }
    for (let gx = stageLeft + 1; gx < stageRight; gx++) {
      if (sandBottom[gx] < 0) continue;
      const left = sandBottom[gx - 1];
      const right = sandBottom[gx + 1];
      if (left < 0 || right < 0) continue;
      const avg = (left + sandBottom[gx] + right) / 3;
      sandBottom[gx] = Math.round(avg);
    }
  }

  // --- 6. Fill sand cells ---
  // Fill ALL empty cells between sandTop and sandBottom — wireframe cells stay,
  // getting completely buried inside the sand envelope
  for (let gx = stageLeft; gx <= stageRight; gx++) {
    const top = sandTop[gx];
    const bottom = sandBottom[gx];
    if (top < 0 || bottom < 0) continue;

    for (let gy = top; gy <= bottom; gy++) {
      const idx = gy * TERRAIN.GRID_WIDTH + gx;
      if (grid[idx] === CELL.EMPTY) {
        grid[idx] = CELL.SAND;
      }
      // WIREFRAME cells stay — they're now buried inside sand
    }
  }

  // --- 7. CA cleanup ---
  for (let iter = 0; iter < SAND_GEN.SMOOTH_ITERATIONS; iter++) {
    // Pass A: remove isolated sand cells (sand with ≤1 solid neighbor)
    for (let gy = 1; gy < gridHeight - 1; gy++) {
      const rowOffset = gy * TERRAIN.GRID_WIDTH;
      for (let gx = 1; gx < TERRAIN.GRID_WIDTH - 1; gx++) {
        const idx = rowOffset + gx;
        if (grid[idx] !== CELL.SAND) continue;

        const hasEmpty =
          grid[idx - 1] === CELL.EMPTY ||
          grid[idx + 1] === CELL.EMPTY ||
          grid[idx - TERRAIN.GRID_WIDTH] === CELL.EMPTY ||
          grid[idx + TERRAIN.GRID_WIDTH] === CELL.EMPTY;

        if (!hasEmpty) continue;

        let solidCount = 0;
        if (grid[idx - 1] > 0) solidCount++;
        if (grid[idx + 1] > 0) solidCount++;
        if (grid[idx - TERRAIN.GRID_WIDTH] > 0) solidCount++;
        if (grid[idx + TERRAIN.GRID_WIDTH] > 0) solidCount++;

        if (solidCount <= 1) {
          grid[idx] = CELL.EMPTY;
        }
      }
    }

    // Pass B: fill gaps (empty cells with ≥3 solid neighbors)
    for (let gy = 1; gy < gridHeight - 1; gy++) {
      const rowOffset = gy * TERRAIN.GRID_WIDTH;
      for (let gx = 1; gx < TERRAIN.GRID_WIDTH - 1; gx++) {
        const idx = rowOffset + gx;
        if (grid[idx] !== CELL.EMPTY) continue;

        let solidCount = 0;
        if (grid[idx - 1] > 0) solidCount++;
        if (grid[idx + 1] > 0) solidCount++;
        if (grid[idx - TERRAIN.GRID_WIDTH] > 0) solidCount++;
        if (grid[idx + TERRAIN.GRID_WIDTH] > 0) solidCount++;

        if (solidCount >= 3) {
          grid[idx] = CELL.SAND;
        }
      }
    }
  }

  // --- 8. Remove thin sand debris ---
  // Eliminate horizontal sand runs thinner than MIN_THICKNESS
  removeThinSand(stageLeft, stageRight, 0, gridHeight - 1, SAND_GEN.MIN_THICKNESS);
}
